import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Edit2, Check, X } from 'lucide-react';
import Modal from '../../components/Modal';
import SortableLinksList from '../../components/SortableLinksList';
import { reorderAdminLinks } from '../../services/apiService';
import { apiClient } from '../../config/api';

const AdminLinksPage = () => {
    const [linksConfig, setLinksConfig] = useState({ sales: [], service: [] });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentType, setCurrentType] = useState(null);
    const [newLink, setNewLink] = useState({ link_key: '', link_label: '', is_required: false });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLinksConfig();
    }, []);

    const fetchLinksConfig = async () => {
        try {
            const data = await apiClient.get('/admin/links');
            // Group the flat array by link_type
            const grouped = data.reduce((acc, link) => {
                const type = link.link_type;
                if (!acc[type]) acc[type] = [];
                acc[type].push(link);
                return acc;
            }, { sales: [], service: [] });
            setLinksConfig(grouped);
        } catch (error) {
            console.error('Error fetching links config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (type, linkId, field, value) => {
        try {
            // Get the current link to include all required fields
            const currentLink = linksConfig[type].find(link => link.id === linkId);

            const updatedLink = await apiClient.put(`/admin/links/${linkId}`, {
                ...currentLink,
                [field]: value
            });
            setLinksConfig(prev => ({
                ...prev,
                [type]: prev[type].map(link =>
                    link.id === linkId ? updatedLink : link
                )
            }));
        } catch (error) {
            console.error('Error updating link:', error);
        }
    };


    const handleDelete = async (type, linkId) => {
        if (!window.confirm('Are you sure you want to delete this link?')) return;

        try {
            await apiClient.delete(`/admin/links/${linkId}`);
            setLinksConfig(prev => ({
                ...prev,
                [type]: prev[type].filter(link => link.id !== linkId)
            }));
        } catch (error) {
            console.error('Error deleting link:', error);
        }
    };

    const handleAdd = async () => {
        if (!newLink.link_key || !newLink.link_label) return;

        try {
            const createdLink = await apiClient.post('/admin/links', {
                link_type: currentType,
                link_key: newLink.link_key,
                link_label: newLink.link_label,
                is_required: newLink.is_required
            });
            setLinksConfig(prev => ({
                ...prev,
                [currentType]: [...prev[currentType], createdLink]
            }));
            setIsModalOpen(false);
            setNewLink({ link_key: '', link_label: '', is_required: false });
        } catch (error) {
            console.error('Error adding link:', error);
        }
    };

    const openAddModal = (type) => {
        setCurrentType(type);
        setNewLink({ link_key: '', link_label: '', is_required: false });
        setIsModalOpen(true);
    };

    const handleReorder = async (type, orderedIds) => {
        try {
            await reorderAdminLinks(type, orderedIds);
            // Refetch the data to get the updated display_order values
            await fetchLinksConfig();
        } catch (error) {
            console.error('Error reordering links:', error);
        }
    };

    const handleRename = async (type, linkId, newLabel) => {
        await handleUpdate(type, linkId, 'link_label', newLabel);
    };

    const handleToggleActive = async (type, linkId, isActive) => {
        await handleUpdate(type, linkId, 'is_active', isActive);
    };

    const handleToggleRequired = async (type, linkId, isRequired) => {
        await handleUpdate(type, linkId, 'is_required', isRequired);
    };

    const renderLinksSection = (type) => (
        <SortableLinksList
            data={linksConfig[type]}
            title={type}
            onDelete={(linkId) => handleDelete(type, linkId)}
            onReorder={(orderedIds) => handleReorder(type, orderedIds)}
            onAdd={() => openAddModal(type)}
            onRename={(linkId, newLabel) => handleRename(type, linkId, newLabel)}
            onToggleActive={(linkId, isActive) => handleToggleActive(type, linkId, isActive)}
            onToggleRequired={(linkId, isRequired) => handleToggleRequired(type, linkId, isRequired)}
        />
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">Loading links configuration...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Links Configuration</h3>
            </div>

            <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-lg font-semibold mb-3 capitalize">Sales Links</h4>
                    {renderLinksSection('sales')}
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h4 className="text-lg font-semibold mb-3 capitalize">Service Links</h4>
                    {renderLinksSection('service')}
                </div>
            </div>

            <div className="text-xs text-gray-500 mt-4">
                Click the edit icon to modify fields.
                <br />
                Drag the grip icon to reorder links.
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={`Add New ${currentType} Link`}>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Link Key</label>
                        <input
                            type="text"
                            value={newLink.link_key}
                            onChange={e => setNewLink({ ...newLink, link_key: e.target.value })}
                            className="w-full p-2 border rounded"
                            placeholder="e.g., website_url, facebook_url"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            This is the database field name (use underscores, lowercase)
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Display Label</label>
                        <input
                            type="text"
                            value={newLink.link_label}
                            onChange={e => setNewLink({ ...newLink, link_label: e.target.value })}
                            className="w-full p-2 border rounded"
                            placeholder="e.g., Visit Website, Facebook Page"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            This is what users will see in the setup interface
                        </p>
                    </div>
                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={newLink.is_required}
                                onChange={e => setNewLink({ ...newLink, is_required: e.target.checked })}
                                className="rounded"
                            />
                            <span className="text-sm font-medium">Required Field</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                            When enabled, this link must be filled in by setup users
                        </p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="px-4 py-2 text-sm border rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAdd}
                            disabled={!newLink.link_key || !newLink.link_label}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:bg-gray-300"
                        >
                            Add Link
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminLinksPage;